import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Search, GraduationCap, Youtube } from "lucide-react";
import { api } from "@/lib/api";

const links = [
  { to: "/", label: "Home", hindi: "होम" },
  { to: "/classes", label: "Classes" },
  { to: "/subjects", label: "Subjects" },
  { to: "/notes", label: "Notes" },
  { to: "/mcq", label: "MCQ Quiz" },
  { to: "/important-questions", label: "Important Qs" },
  { to: "/previous-year-questions", label: "PYQ" },
  { to: "/online-tests", label: "Online Tests" },
  { to: "/youtube", label: "YouTube" },
  { to: "/blog", label: "Blog" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [settings, setSettings] = useState({});
  const nav = useNavigate();
  useEffect(() => { api.get("/settings/public").then((r) => setSettings(r.data || {})).catch(() => {}); }, []);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim().length >= 2) { nav(`/search?q=${encodeURIComponent(q.trim())}`); setOpen(false); }
  };

  return (
    <>
      <div className="bg-[#DC2626] text-white text-xs sm:text-sm py-1.5 px-3 text-center font-medium" data-testid="top-bar">
        <span className="font-hindi">बिहार बोर्ड 2027 की तैयारी — निःशुल्क Notes, MCQ, Test एवं YouTube Classes</span>
      </div>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="vr-container flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-9 h-9 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center font-black">
              <span className="text-sm">VR</span>
            </div>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-[#0F172A] text-lg">VR Classes</div>
              <div className="text-[10px] text-slate-500 font-medium">Bihar Board Preparation</div>
            </div>
          </Link>
          <form onSubmit={submit} className="hidden md:flex items-center bg-slate-100 border border-slate-200 rounded-lg h-10 px-3 w-72" data-testid="search-form-desktop">
            <Search size={16} className="text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notes, MCQ, blog..." className="bg-transparent outline-none px-2 text-sm w-full" data-testid="search-input-desktop" />
          </form>
          <div className="hidden lg:flex items-center gap-1">
            {settings?.youtube_channel_url && (
              <a href={settings.youtube_channel_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#DC2626] text-white text-sm font-semibold hover:bg-[#B91C1C]" data-testid="header-youtube-btn">
                <Youtube size={16} /> YouTube
              </a>
            )}
            <Link to="/english-course" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#1E3A8A] text-white text-sm font-semibold hover:bg-[#152a63]" data-testid="header-course-btn">
              <GraduationCap size={16} /> English {settings.course_price_inr ? `₹${settings.course_price_inr}` : "Course"}
            </Link>
          </div>
          <button onClick={() => setOpen(true)} className="lg:hidden h-10 w-10 flex items-center justify-center rounded-md border border-slate-200" data-testid="mobile-menu-open">
            <Menu size={20} />
          </button>
        </div>
        <nav className="hidden lg:block border-t border-slate-100 bg-white">
          <div className="vr-container flex items-center gap-1 overflow-x-auto py-1">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} className={({ isActive }) => `whitespace-nowrap px-3 py-2 text-sm font-semibold rounded-md ${isActive ? "text-[#1E3A8A] bg-blue-50" : "text-slate-700 hover:text-[#1E3A8A] hover:bg-slate-50"}`} data-testid={`nav-${l.to.replace('/', '') || 'home'}`}>
                {l.label}
              </NavLink>
            ))}
            <NavLink to="/english-course" className={({ isActive }) => `whitespace-nowrap px-3 py-2 text-sm font-semibold rounded-md ${isActive ? "text-[#1E3A8A] bg-blue-50" : "text-slate-700 hover:text-[#1E3A8A] hover:bg-slate-50"}`} data-testid="nav-english-course">
              English {settings.course_price_inr ? `₹${settings.course_price_inr}` : "Course"}
            </NavLink>
          </div>
        </nav>
      </header>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[86%] max-w-sm bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="font-display font-extrabold text-lg text-[#1E3A8A]">VR Classes</div>
              <button onClick={() => setOpen(false)} className="h-10 w-10 flex items-center justify-center rounded-md border" data-testid="mobile-menu-close"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="m-4 flex items-center bg-slate-100 rounded-lg h-11 px-3">
              <Search size={16} className="text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="bg-transparent outline-none px-2 text-sm w-full" data-testid="search-input-mobile" />
            </form>
            <div className="overflow-y-auto flex-1 pb-8">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} end={l.to === "/"} className={({ isActive }) => `block px-4 py-3 border-b border-slate-100 text-sm font-semibold ${isActive ? "text-[#1E3A8A] bg-blue-50" : "text-slate-800"}`} data-testid={`m-nav-${l.to.replace('/', '') || 'home'}`}>{l.label}</NavLink>
              ))}
              <div className="p-4 space-y-2">
                <Link to="/english-course" onClick={() => setOpen(false)} className="vr-btn-primary w-full" data-testid="m-course-cta">English Course {settings.course_price_inr ? `₹${settings.course_price_inr}` : ""}</Link>
                {settings?.youtube_channel_url && (
                  <a href={settings.youtube_channel_url} target="_blank" rel="noreferrer" className="vr-btn-red w-full" data-testid="m-youtube-cta"><Youtube size={16} /> YouTube Channel</a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
